import { ReactElement, useState, useCallback } from 'react';
import { useRoutes, useAwsEffect, useAwsGetter } from '@localstack/services';
import { DEFAULT_API_GATEWAY_ROUTES } from '@localstack/constants';
import { Card, Button } from '@mui/material';
import { useParams } from 'react-router-dom';

import { PageTitle, Dropdown, ButtonWithDialog, AgwApiIntegrationsTable } from '@localstack/ui';

import { ApiGatewayProps } from './types';
import { ApiNavTabs } from './components';

export const AgwApiIntegrations = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_API_GATEWAY_ROUTES,
}: ApiGatewayProps): ReactElement => {
  const { apiId: ApiId } = useParams<'apiId'>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { goto } = useRoutes();
  const { data: api } = useAwsGetter('ApiGatewayV2', 'getApi', [{ ApiId }], { clientOverrides });

  const { data: integrations, mutate } = useAwsGetter('ApiGatewayV2', 'getIntegrations', [{ ApiId }], {
    clientOverrides,
  });

  const { deleteIntegration, isLoading } = useAwsEffect('ApiGatewayV2', ['deleteIntegration'], {
    revalidate: ['getIntegration', 'getIntegrations'],
    clientOverrides,
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((IntegrationId) => deleteIntegration({ ApiId: ApiId as string, IntegrationId }));
    await Promise.all(promises);
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="API Integrations"
      title={
        <PageTitle
          title="API Integrations"
          onMutate={mutate}
          breadcrumbs={[
            ['API Gateway', () => goto(routes.RESOURCES_AGW2)],
            [api?.Name, () => goto(routes.RESOURCES_AGW2_API, { apiId: ApiId })],
            ['Integrations', null],
          ]}
        />
      }
      tabs={<ApiNavTabs apiId={ApiId as string} routes={routes} />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={() => goto(routes.RESOURCES_AGW2_API_INTEGRATION_CREATE, { apiId: ApiId })}
          >
            Create Integration
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Integrations(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Integrations will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <AgwApiIntegrationsTable
          integrations={integrations?.Items || []}
          onSelect={setSelectedIds}
          onViewIntegration={({ IntegrationId }) =>
            goto(routes.RESOURCES_AGW2_API_INTEGRATION, { apiId: ApiId, integrationId: IntegrationId })
          }
        />
      </Card>
    </Layout>
  );
};
